import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { AlertTriangle, Star, User, Loader2 } from 'lucide-react';

/**
 * iter321g — Prompts the admin when a child is linked to multiple parents.
 *
 * The backend refuses with a 409 `multi_parent_confirm_required` payload
 * that lists every linked parent (with `is_current_primary` flagged).
 * The admin picks who should own this enrollment; if they pick a
 * non-primary parent, that parent gets promoted to primary on the
 * player record before the enrollment is created.
 *
 * Props:
 *   open              — bool
 *   prompt            — the 409 detail (player_name, candidates[])
 *   loading           — bool while the retry is in flight
 *   onCancel()        — close without action
 *   onConfirm(pid)    — retry submit with `confirm_primary_parent_id = pid`
 */
export default function MultiParentEnrollPromptDialog({
  open, prompt, loading, onCancel, onConfirm,
}) {
  if (!prompt) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-lg" data-testid="multi-parent-prompt">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Pick the primary parent
          </DialogTitle>
          <DialogDescription>
            {prompt.player_name || 'This child'} is linked to{' '}
            <strong>{prompt.candidates?.length || 0} parents</strong>.
            Enrollments (and future billing) will land on whoever you pick.
            The selection promotes that parent to primary on the child's record.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {(prompt.candidates || []).map((c) => (
            <button
              key={c.id}
              type="button"
              disabled={loading}
              onClick={() => onConfirm(c.id)}
              data-testid={`multi-parent-choice-${c.id}`}
              className="w-full flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white p-3 text-left hover:border-slate-400 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                  <User className="h-4 w-4 text-slate-600" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 truncate">
                      {c.name || '(no name)'}
                    </span>
                    {c.is_current_primary && (
                      <Badge className="bg-amber-100 text-amber-800 gap-1">
                        <Star className="h-3 w-3" /> Current primary
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {c.email || '—'}
                    {c.phone ? ` · ${c.phone}` : ''}
                  </div>
                </div>
              </div>
              {loading && (
                <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
              )}
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={loading}
            data-testid="multi-parent-cancel"
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
